import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  DownloadSimple, FilePdf, Robot, Trash, Check, X, PencilSimple, ArrowsClockwise,
  FunnelSimple, TrashSimple, CaretUp, CaretDown, ArrowsDownUp, EnvelopeSimple, PaperPlaneTilt,
  MagnifyingGlass,
} from "@phosphor-icons/react";

const ALL = "__all__";

const sourceBadge = {
  heuristik: { label: "Heuristik", cls: "bg-zinc-100 text-zinc-600" },
  ki: { label: "KI", cls: "bg-[#002FA7]/10 text-[#002FA7]" },
  manuell: { label: "Manuell", cls: "bg-[#00796B]/10 text-[#00796B]" },
};

const statusBadge = {
  New: "bg-blue-50 text-[#002FA7] border border-[#002FA7]/20",
  Prepared: "bg-amber-50 text-amber-700 border border-amber-200",
  Sent: "bg-[#00796B]/10 text-[#00796B] border border-[#00796B]/20",
  "E-mail Versand": "bg-violet-50 text-violet-700 border border-violet-200",
  "Rückläufer": "bg-orange-50 text-orange-700 border border-orange-200",
  "E-Mail bounced": "bg-red-50 text-[#E53935] border border-red-200",
};
const STATUS_OPTIONS = ["New", "Prepared", "Sent", "E-mail Versand", "Rückläufer", "E-Mail bounced"];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

// Parse dd.mm.yyyy -> sortable yyyymmdd number (0 if unparseable)
const parseGermanDate = (s) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec((s || "").trim());
  return m ? Number(`${m[3]}${m[2]}${m[1]}`) : 0;
};

const getSortValue = (lead, key) => {
  switch (key) {
    case "lead_no": return lead.lead_no ?? 0;
    case "created_at": return lead.created_at || "";
    case "gruendungsdatum": return parseGermanDate(lead.gruendungsdatum);
    case "plz": return lead.plz || "";
    case "geschaeftsfuehrung": return (lead.geschaeftsfuehrung || []).map((p) => p.name).join(", ").toLowerCase();
    case "tags": return (lead.tags || []).join(", ").toLowerCase();
    case "strasse": return (lead.strasse || lead.street || "").toLowerCase();
    case "rating": return lead.rating ?? -1;
    default: return (lead[key] ?? "").toString().toLowerCase();
  }
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [pdfDialog, setPdfDialog] = useState(false);
  const [chosenTemplate, setChosenTemplate] = useState("");
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [chosenMailbox, setChosenMailbox] = useState("");
  const [emailPrompts, setEmailPrompts] = useState([]);
  const [chosenPrompt, setChosenPrompt] = useState("");

  // filters
  const [fBundesland, setFBundesland] = useState(ALL);
  const [fLandkreis, setFLandkreis] = useState(ALL);
  const [fTag, setFTag] = useState(ALL);
  const [fStatus, setFStatus] = useState(ALL);
  const [fSource, setFSource] = useState(ALL);
  const [fNameSource, setFNameSource] = useState(ALL);
  const [progress, setProgress] = useState(null);
  const [fSearch, setFSearch] = useState("");
  const [fPlz, setFPlz] = useState("");
  const [fStrasse, setFStrasse] = useState("");
  const [fDateFrom, setFDateFrom] = useState("");
  const [fDateTo, setFDateTo] = useState("");

  // sorting
  const [sortKey, setSortKey] = useState("lead_no");
  const [sortDir, setSortDir] = useState("desc");

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/mailboxes").then((r) => setMailboxes(r.data)).catch(() => {});
    api.get("/email-prompts").then((r) => {
      setEmailPrompts(r.data);
      if (r.data.length > 0) setChosenPrompt(r.data[0].id);
    }).catch(() => {});
  }, [load]);

  const bundeslaender = useMemo(
    () => [...new Set(leads.map((l) => l.bundesland).filter(Boolean))].sort(),
    [leads]
  );
  const landkreise = useMemo(
    () =>
      [...new Set(
        leads
          .filter((l) => fBundesland === ALL || l.bundesland === fBundesland)
          .map((l) => l.landkreis)
          .filter(Boolean)
      )].sort(),
    [leads, fBundesland]
  );
  const allTags = useMemo(
    () => [...new Set(leads.flatMap((l) => l.tags || []))].sort(),
    [leads]
  );
  const sources = useMemo(
    () => [...new Set(leads.map((l) => l.source_label).filter(Boolean))].sort(),
    [leads]
  );

  const filtered = useMemo(
    () =>
      leads.filter((l) => {
        if (fSearch.trim()) {
          const hay = [
            l.name, l.name_for_emails, l.email, l.phone, l.website,
            l.street, l.strasse, l.hausnummer, l.plz, l.city,
            l.firmentyp, l.beschreibung, l.source_label, l.gruendungsdatum,
            l.hrb, l.euid, l.bundesland, l.landkreis,
            ...(l.tags || []),
            ...((l.geschaeftsfuehrung || []).map((p) => p.name)),
          ].filter(Boolean).join(" ").toLowerCase();
          const terms = fSearch.toLowerCase().split(/\s+/).filter(Boolean);
          if (!terms.every((t) => hay.includes(t))) return false;
        }
        if (fBundesland !== ALL && l.bundesland !== fBundesland) return false;
        if (fLandkreis !== ALL && (l.landkreis || "") !== fLandkreis) return false;
        if (fTag !== ALL && !(l.tags || []).includes(fTag)) return false;
        if (fStatus !== ALL && (l.status || "New") !== fStatus) return false;
        if (fSource !== ALL && (l.source_label || "") !== fSource) return false;
        if (fNameSource !== ALL && (l.name_source || "") !== fNameSource) return false;
        if (fPlz && !(l.plz || "").includes(fPlz)) return false;
        if (fStrasse && !((l.strasse || l.street || "").toLowerCase().includes(fStrasse.toLowerCase()))) return false;
        if (fDateFrom || fDateTo) {
          const d = (l.created_at || "").slice(0, 10);
          if (fDateFrom && d < fDateFrom) return false;
          if (fDateTo && d > fDateTo) return false;
        }
        return true;
      }),
    [leads, fSearch, fBundesland, fLandkreis, fTag, fStatus, fSource, fNameSource, fPlz, fStrasse, fDateFrom, fDateTo]
  );

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "de") * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const resetFilters = () => {
    setFSearch("");
    setFBundesland(ALL); setFLandkreis(ALL); setFTag(ALL); setFStatus(ALL); setFSource(ALL); setFNameSource(ALL);
    setFPlz(""); setFStrasse(""); setFDateFrom(""); setFDateTo("");
  };
  const hasFilters = fSearch || fBundesland !== ALL || fLandkreis !== ALL || fTag !== ALL || fStatus !== ALL || fSource !== ALL || fNameSource !== ALL || fPlz || fStrasse || fDateFrom || fDateTo;

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAll = () => {
    const ids = filtered.map((l) => l.id);
    const allSel = ids.every((id) => selected.has(id)) && ids.length > 0;
    setSelected(allSel ? new Set() : new Set(ids));
  };

  const startEdit = (lead) => {
    setEditing(lead.id);
    setEditValue(lead.name_for_emails || "");
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { name_for_emails: editValue });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      setEditing(null);
      toast.success("Markenname gespeichert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeLead = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelected((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    toast.success("Lead gelöscht");
  };

  const bulkStatus = async (status) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/leads/status-bulk", {
        lead_ids: Array.from(selected),
        status,
      });
      setLeads((ls) => ls.map((l) => (selected.has(l.id) ? { ...l, status } : l)));
      toast.success(`${data.matched} Leads auf „${status}" gesetzt`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/leads/delete-bulk", ids);
      setLeads((ls) => ls.filter((l) => !selected.has(l.id)));
      setSelected(new Set());
      toast.success(`${data.deleted} Leads gelöscht`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/leads");
      setLeads([]);
      setSelected(new Set());
      setDeleteAllDialog(false);
      toast.success(`${data.deleted} Leads gelöscht`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deriveNames = async () => {
    if (selected.size === 0) return toast.error("Bitte zuerst Leads auswählen");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/derive-names", Array.from(selected));
      setProgress({ label: "KI-Namen", done: 0, total: data.total });
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/leads/name-jobs/${data.job_id}`);
          setProgress({ label: "KI-Namen", done: r.data.done, total: r.data.total });
          if (r.data.status === "done") {
            clearInterval(poll);
            setProgress(null);
            toast.success(`${r.data.updated} von ${r.data.total} Namen per KI abgeleitet`);
            await load();
            setBusy(false);
          } else if (r.data.status === "error") {
            clearInterval(poll);
            setProgress(null);
            toast.error("KI-Ableitung unterbrochen. Bitte erneut starten.");
            await load();
            setBusy(false);
          }
        } catch (e) {
          clearInterval(poll);
          setProgress(null);
          toast.error("KI-Ableitung fehlgeschlagen");
          setBusy(false);
        }
      }, 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  // Selected leads take precedence; otherwise the currently filtered + sorted list is used.
  const effectiveLeads = useMemo(
    () => (selected.size > 0 ? sorted.filter((l) => selected.has(l.id)) : sorted),
    [sorted, selected]
  );
  const effectiveIds = useMemo(() => effectiveLeads.map((l) => l.id), [effectiveLeads]);
  const emailLeadCount = useMemo(
    () => effectiveLeads.filter((l) => (l.email || "").trim()).length,
    [effectiveLeads]
  );

  const exportCsv = async () => {
    try {
      const res = await api.post("/leads/export.csv", { lead_ids: effectiveIds }, { responseType: "blob" });
      downloadBlob(res.data, "empfaenger.csv");
      toast.success(
        selected.size > 0
          ? `CSV mit ${effectiveIds.length} ausgewählten Leads exportiert`
          : `CSV mit ${effectiveIds.length} gefilterten Leads exportiert`
      );
    } catch (err) {
      toast.error("Export fehlgeschlagen");
    }
  };

  const findEmails = async () => {
    if (effectiveIds.length === 0) return toast.error("Keine Leads vorhanden");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/find-emails", { lead_ids: effectiveIds });
      toast.info(`E-Mail-Suche gestartet für ${data.total} Leads mit Website…`);
      setProgress({ label: "E-Mail-Suche", done: 0, total: data.total });
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/leads/email-jobs/${data.job_id}`);
          setProgress({ label: "E-Mail-Suche", done: r.data.done, total: r.data.total });
          if (r.data.status === "done") {
            clearInterval(poll);
            setProgress(null);
            toast.success(`${r.data.found} ${r.data.found === 1 ? "E-Mail-Adresse" : "E-Mail-Adressen"} gefunden (von ${r.data.total} Websites)`);
            await load();
            setBusy(false);
          } else if (r.data.status === "error") {
            clearInterval(poll);
            setProgress(null);
            toast.error("E-Mail-Suche unterbrochen. Bitte erneut starten.");
            setBusy(false);
          }
        } catch (e) {
          clearInterval(poll);
          setProgress(null);
          toast.error("E-Mail-Suche fehlgeschlagen");
          setBusy(false);
        }
      }, 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "E-Mail-Suche fehlgeschlagen");
      setBusy(false);
    }
  };

  const generateEmails = async () => {
    if (!chosenMailbox) return toast.error("Bitte ein Postfach wählen");
    if (!chosenPrompt) return toast.error("Bitte einen Prompt wählen");
    setBusy(true);
    try {
      const { data } = await api.post("/emails/generate", {
        lead_ids: effectiveIds,
        mailbox_id: chosenMailbox,
        prompt_id: chosenPrompt,
      });
      setEmailDialog(false);
      setProgress({ label: "E-Mail-Generierung", done: 0, total: data.total });
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/emails/generate-jobs/${data.job_id}`);
          setProgress({ label: "E-Mail-Generierung", done: r.data.done, total: r.data.total });
          if (r.data.status === "done") {
            clearInterval(poll);
            setProgress(null);
            toast.success(`${r.data.created} E-Mails generiert – unter „E-Mail Versand" prüfen & versenden`);
            setBusy(false);
          } else if (r.data.status === "error") {
            clearInterval(poll);
            setProgress(null);
            toast.error("E-Mail-Generierung unterbrochen. Bitte erneut starten.");
            setBusy(false);
          }
        } catch (e) {
          clearInterval(poll);
          setProgress(null);
          toast.error("E-Mail-Generierung fehlgeschlagen");
          setBusy(false);
        }
      }, 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "E-Mail-Generierung fehlgeschlagen");
      setBusy(false);
    }
  };

  const generatePdf = async () => {
    if (!chosenTemplate) return toast.error("Bitte eine Vorlage wählen");
    if (effectiveIds.length === 0) return toast.error("Keine Empfänger vorhanden");
    setBusy(true);
    try {
      const { data } = await api.post("/generate-pdf", {
        template_id: chosenTemplate,
        lead_ids: effectiveIds,
      });
      const bid = data.batch_id;
      setPdfDialog(false);
      toast.info(`PDF wird erstellt (${data.lead_count} Briefe)… Das kann bei vielen Leads etwas dauern.`);
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const r = await api.get(`/versand/${bid}`);
          if (r.data.status === "ready") {
            clearInterval(poll);
            const pdf = await api.get(`/versand/${bid}/pdf`, { responseType: "blob" });
            downloadBlob(pdf.data, `${r.data.name}.pdf`);
            toast.success(`Serienbrief mit ${r.data.lead_count} Briefen erstellt`);
            await load();
            setBusy(false);
          } else if (r.data.status === "error") {
            clearInterval(poll);
            toast.error("PDF-Erstellung fehlgeschlagen: " + (r.data.error || ""));
            setBusy(false);
          } else if (tries > 450) {
            clearInterval(poll);
            toast.error("Zeitüberschreitung bei der PDF-Erstellung");
            setBusy(false);
          }
        } catch (e) {
          clearInterval(poll);
          toast.error("PDF-Erstellung fehlgeschlagen");
          setBusy(false);
        }
      }, 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "PDF-Erstellung fehlgeschlagen");
      setBusy(false);
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((l) => selected.has(l.id));

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Leads</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {filtered.length} von {leads.length} angezeigt · {selected.size} ausgewählt
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={findEmails} disabled={busy || effectiveIds.length === 0} data-testid="find-emails-button" className="rounded-sm border-zinc-300">
            <EnvelopeSimple size={16} className="mr-2" /> E-Mails finden
          </Button>
          <Button variant="outline" onClick={() => setEmailDialog(true)} disabled={busy || emailLeadCount === 0} data-testid="generate-emails-button" className="rounded-sm border-zinc-300">
            <PaperPlaneTilt size={16} className="mr-2" /> E-Mails generieren
          </Button>
          <Button variant="outline" onClick={deriveNames} disabled={busy || selected.size === 0} data-testid="derive-names-button" className="rounded-sm border-zinc-300">
            <Robot size={16} className="mr-2" /> KI-Namen ableiten
          </Button>
          <Select value="" onValueChange={bulkStatus} disabled={busy || selected.size === 0}>
            <SelectTrigger className="w-[180px] h-9 rounded-sm border-zinc-300" data-testid="bulk-status-select">
              <SelectValue placeholder={selected.size > 0 ? `Status setzen (${selected.size})` : "Status setzen"} />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={deleteSelected} disabled={busy || selected.size === 0} data-testid="delete-selected-button" className="rounded-sm border-zinc-300 text-[#E53935] hover:text-[#E53935]">
            <Trash size={16} className="mr-2" /> Ausgewählte löschen
          </Button>
          <Button variant="outline" onClick={() => setDeleteAllDialog(true)} disabled={busy || leads.length === 0} data-testid="delete-all-button" className="rounded-sm border-zinc-300 text-[#E53935] hover:text-[#E53935]">
            <TrashSimple size={16} className="mr-2" /> Alle löschen
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={effectiveIds.length === 0} data-testid="export-csv-button" className="rounded-sm border-zinc-300">
            <DownloadSimple size={16} className="mr-2" /> CSV Export {selected.size > 0 ? `(${selected.size})` : `(${filtered.length} gefiltert)`}
          </Button>
          <Button onClick={() => setPdfDialog(true)} disabled={effectiveIds.length === 0} data-testid="open-pdf-dialog-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
            <FilePdf size={16} className="mr-2" /> PDF generieren
          </Button>
        </div>
      </div>

      {/* Progress bar for background jobs (AI names, email finder) */}
      {progress && (
        <div className="bg-white border border-[#002FA7]/30 rounded-md p-4 mb-5" data-testid="job-progress">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="font-medium text-zinc-700">{progress.label} läuft…</span>
            <span className="text-zinc-500 tabular-nums" data-testid="job-progress-count">{progress.done} / {progress.total}</span>
          </div>
          <Progress value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0} className="h-2" />
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="filter-bar">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500">
          <FunnelSimple size={16} /> Filter
        </div>
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Volltextsuche…" value={fSearch} onChange={(e) => setFSearch(e.target.value)} className="w-56 h-9 pl-8" data-testid="filter-search" />
          {fSearch && (
            <button onClick={() => setFSearch("")} data-testid="filter-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={fBundesland} onValueChange={(v) => { setFBundesland(v); setFLandkreis(ALL); }}>
          <SelectTrigger className="w-44 h-9 rounded-sm" data-testid="filter-bundesland"><SelectValue placeholder="Bundesland" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Bundesländer</SelectItem>
            {bundeslaender.map((b) => <SelectItem key={b} value={b}>{cap(b)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fLandkreis} onValueChange={setFLandkreis}>
          <SelectTrigger className="w-48 h-9 rounded-sm" data-testid="filter-landkreis"><SelectValue placeholder="Landkreis" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Landkreise</SelectItem>
            {landkreise.map((b) => <SelectItem key={b} value={b}>{cap(b.replace(/-/g, " "))}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="PLZ" value={fPlz} onChange={(e) => setFPlz(e.target.value)} className="w-24 h-9" data-testid="filter-plz" />
        <Input placeholder="Straße" value={fStrasse} onChange={(e) => setFStrasse(e.target.value)} className="w-40 h-9" data-testid="filter-strasse" />
        <Select value={fTag} onValueChange={setFTag}>
          <SelectTrigger className="w-44 h-9 rounded-sm" data-testid="filter-tag"><SelectValue placeholder="Tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Tags</SelectItem>
            {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-36 h-9 rounded-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fSource} onValueChange={setFSource}>
          <SelectTrigger className="w-44 h-9 rounded-sm" data-testid="filter-source"><SelectValue placeholder="Quelle" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Quellen</SelectItem>
            {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fNameSource} onValueChange={setFNameSource}>
          <SelectTrigger className="w-44 h-9 rounded-sm" data-testid="filter-name-source"><SelectValue placeholder="Namensherkunft" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Namensherkünfte</SelectItem>
            <SelectItem value="heuristik">Heuristik</SelectItem>
            <SelectItem value="ki">KI</SelectItem>
            <SelectItem value="manuell">Manuell</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-zinc-400">Datum</span>
          <Input type="date" value={fDateFrom} onChange={(e) => setFDateFrom(e.target.value)} className="w-[140px] h-9" data-testid="filter-date-from" />
          <span className="text-xs text-zinc-400">–</span>
          <Input type="date" value={fDateTo} onChange={(e) => setFDateTo(e.target.value)} className="w-[140px] h-9" data-testid="filter-date-to" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="filter-reset" className="h-9 text-xs">
            <X size={14} className="mr-1" /> Zurücksetzen
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-12 text-center">Lade Leads…</div>
      ) : leads.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center text-zinc-500">
          Noch keine Leads. Starte eine Recherche, um Neugründungen zu finden.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm border-collapse min-w-[2000px]">
            <thead>
              <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-3 w-10">
                  <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                </th>
                <SortTh label="ID" col="lead_no" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Firma" col="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Markenname" col="name_for_emails" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="E-Mail" col="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Telefon" col="phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Website" col="website" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Bewertung" col="rating" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Quelle" col="source_label" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="PLZ" col="plz" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Stadt" col="city" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Straße" col="strasse" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Nr." col="hausnummer" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Typ" col="firmentyp" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Gegründet" col="gruendungsdatum" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Geschäftsführung" col="geschaeftsfuehrung" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Tags" col="tags" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Hinzugefügt" col="created_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((l) => (
                <tr key={l.id} data-testid="leads-table-row" className={`border-t border-zinc-100 hover:bg-zinc-50 ${selected.has(l.id) ? "bg-[#002FA7]/5" : ""}`}>
                  <td className="px-3 py-2.5">
                    <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`row-checkbox-${l.id}`} />
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400 tabular-nums text-xs">#{String(l.lead_no ?? "").padStart(4, "0")}</td>
                  <td className="px-3 py-2.5 font-medium max-w-[220px]"><div className="truncate" title={l.name}>{l.name}</div></td>
                  <td className="px-3 py-2.5">
                    {editing === l.id ? (
                      <div className="flex items-center gap-1">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-8 text-sm w-36" data-testid={`edit-input-${l.id}`} autoFocus onKeyDown={(e) => e.key === "Enter" && saveEdit(l.id)} />
                        <button onClick={() => saveEdit(l.id)} data-testid={`save-edit-${l.id}`} className="text-[#00796B]"><Check size={16} weight="bold" /></button>
                        <button onClick={() => setEditing(null)} className="text-zinc-400"><X size={16} weight="bold" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group">
                        <span>{l.name_for_emails}</span>
                        <button onClick={() => startEdit(l)} data-testid={`edit-name-${l.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-[#002FA7]"><PencilSimple size={14} /></button>
                        {l.name_source && sourceBadge[l.name_source] && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${sourceBadge[l.name_source].cls}`}>{sourceBadge[l.name_source].label}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 max-w-[200px]">
                    <EditableCell lead={l} field="email" label="E-Mail" type="email" onSaved={(d) => setLeads((ls) => ls.map((x) => (x.id === d.id ? d : x)))} />
                  </td>
                  <td className="px-3 py-2.5 max-w-[160px]">
                    <EditableCell lead={l} field="phone" label="Telefon" type="tel" onSaved={(d) => setLeads((ls) => ls.map((x) => (x.id === d.id ? d : x)))} />
                  </td>
                  <td className="px-3 py-2.5 max-w-[180px]">
                    <EditableCell lead={l} field="website" label="Website" type="url" isLink onSaved={(d) => setLeads((ls) => ls.map((x) => (x.id === d.id ? d : x)))} />
                  </td>
                  <td className="px-3 py-2.5 text-zinc-600 whitespace-nowrap text-xs" data-testid={`rating-${l.id}`}>
                    {l.rating != null ? <>★ {l.rating} <span className="text-zinc-400">({l.rating_count ?? 0})</span></> : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <Select value={l.status || "New"} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger data-testid={`status-select-${l.id}`} className={`h-7 w-[135px] rounded-sm text-xs px-2 ${statusBadge[l.status || "New"]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-600 text-xs">{l.source_label}</td>
                  <td className="px-3 py-2.5 text-zinc-600">{l.plz}</td>
                  <td className="px-3 py-2.5 text-zinc-600">{l.city}</td>
                  <td className="px-3 py-2.5 text-zinc-600 max-w-[180px]"><div className="truncate" title={l.strasse}>{l.strasse}</div></td>
                  <td className="px-3 py-2.5 text-zinc-600">{l.hausnummer}</td>
                  <td className="px-3 py-2.5">{l.firmentyp}</td>
                  <td className="px-3 py-2.5 text-zinc-600">{l.gruendungsdatum}</td>
                  <td className="px-3 py-2.5 text-zinc-600 max-w-[180px]">
                    <div className="truncate" title={(l.geschaeftsfuehrung || []).map((p) => p.name).join(", ")}>
                      {(l.geschaeftsfuehrung || []).map((p) => p.name).join(", ")}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 max-w-[160px]">
                    <div className="flex flex-wrap gap-1">
                      {(l.tags || []).slice(0, 2).map((t, i) => <Badge key={i} variant="secondary" className="text-[10px] font-normal rounded-sm">{t}</Badge>)}
                      {(l.tags || []).length > 2 && <span className="text-[10px] text-zinc-400">+{l.tags.length - 2}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-500 text-xs whitespace-nowrap" data-testid={`created-at-${l.id}`}>{fmtDateTime(l.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => removeLead(l.id)} data-testid={`delete-lead-${l.id}`} className="text-zinc-300 hover:text-[#E53935]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={20} className="px-4 py-10 text-center text-zinc-400">Keine Leads passen zu den Filtern.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* E-Mail generation dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="max-w-2xl" data-testid="email-gen-dialog">
          <DialogHeader><DialogTitle className="font-heading">E-Mails per KI generieren</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-500" data-testid="email-gen-info">
              {emailLeadCount} von {effectiveIds.length} {selected.size > 0 ? "ausgewählten" : "gefilterten"} Leads haben eine E-Mail-Adresse.
              Für diese werden personalisierte E-Mails erstellt.
            </p>
            {mailboxes.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
                Noch kein Postfach eingerichtet. Lege zuerst unter „Postfächer" ein SMTP-Postfach an.
              </div>
            ) : (
              <div>
                <Label className="text-xs text-zinc-600">Absender-Postfach</Label>
                <Select value={chosenMailbox} onValueChange={setChosenMailbox}>
                  <SelectTrigger data-testid="mailbox-select" className="rounded-sm mt-1"><SelectValue placeholder="Postfach wählen" /></SelectTrigger>
                  <SelectContent>
                    {mailboxes.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.from_name ? `${m.from_name} · ${m.from_email}` : m.from_email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-zinc-600">KI-Prompt (Vorlage)</Label>
                <Link to="/email-prompts" data-testid="manage-prompts-link" className="text-xs text-[#002FA7] hover:underline">
                  Prompts verwalten
                </Link>
              </div>
              {emailPrompts.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3 mt-1">
                  Noch kein Prompt vorhanden. Lege zuerst unter „E-Mail Prompts" einen Prompt an.
                </div>
              ) : (
                <>
                  <Select value={chosenPrompt} onValueChange={setChosenPrompt}>
                    <SelectTrigger data-testid="prompt-select" className="rounded-sm mt-1"><SelectValue placeholder="Prompt wählen" /></SelectTrigger>
                    <SelectContent>
                      {emailPrompts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {chosenPrompt && (
                    <div className="mt-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-sm p-3 max-h-36 overflow-y-auto whitespace-pre-wrap" data-testid="prompt-preview">
                      {emailPrompts.find((p) => p.id === chosenPrompt)?.prompt}
                    </div>
                  )}
                </>
              )}
              <p className="text-xs text-zinc-400 mt-1">
                Die KI erhält zusätzlich die Lead-Daten (Firma, Markenname, Ort, Beschreibung …).
                Die Signatur des Postfachs wird beim Versand automatisch angehängt.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={generateEmails} disabled={busy || !chosenMailbox || !chosenPrompt || emailLeadCount === 0} data-testid="start-email-gen-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              {busy ? <ArrowsClockwise size={16} className="mr-2 animate-spin" /> : <Robot size={16} className="mr-2" />}
              {emailLeadCount} E-Mails generieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF dialog */}
      <Dialog open={pdfDialog} onOpenChange={setPdfDialog}>
        <DialogContent data-testid="pdf-dialog">
          <DialogHeader><DialogTitle className="font-heading">Serienbrief erstellen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-500" data-testid="pdf-dialog-recipient-info">
              {selected.size > 0
                ? `${effectiveIds.length} manuell ausgewählte Empfänger.`
                : `${effectiveIds.length} Empfänger aus dem aktuellen Filter (keine manuelle Auswahl).`}{" "}
              Wähle eine Vorlage – die Platzhalter werden mit den Lead-Daten gefüllt.
            </p>
            {templates.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
                Noch keine Vorlage vorhanden. Lade zuerst unter „Vorlagen" eine PDF hoch.
              </div>
            ) : (
              <Select value={chosenTemplate} onValueChange={setChosenTemplate}>
                <SelectTrigger data-testid="template-select" className="rounded-sm"><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfDialog(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={generatePdf} disabled={busy || !chosenTemplate} data-testid="generate-pdf-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              {busy ? <ArrowsClockwise size={16} className="mr-2 animate-spin" /> : <FilePdf size={16} className="mr-2" />}
              PDF herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete all confirm */}
      <Dialog open={deleteAllDialog} onOpenChange={setDeleteAllDialog}>
        <DialogContent data-testid="delete-all-dialog">
          <DialogHeader><DialogTitle className="font-heading">Alle Leads löschen?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">
            Dies löscht <span className="font-medium text-[#E53935]">alle {leads.length} Leads</span> unwiderruflich. Vorlagen und Einstellungen bleiben erhalten.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllDialog(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={deleteAll} disabled={busy} data-testid="confirm-delete-all-button" className="bg-[#E53935] hover:bg-red-700 text-white rounded-sm">
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function EditableCell({ lead, field, label, type = "text", isLink = false, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const save = async () => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { [field]: value });
      onSaved(data);
      setEditing(false);
      toast.success(`${label} gespeichert`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input type={type} value={value} onChange={(e) => setValue(e.target.value)} className="h-8 text-sm w-40" data-testid={`edit-${field}-input-${lead.id}`} autoFocus onKeyDown={(e) => e.key === "Enter" && save()} />
        <button onClick={save} data-testid={`save-${field}-${lead.id}`} className="text-[#00796B] p-1"><Check size={16} weight="bold" /></button>
        <button onClick={() => setEditing(false)} className="text-zinc-400 p-1"><X size={16} weight="bold" /></button>
      </div>
    );
  }
  const val = lead[field] || "";
  return (
    <div className="flex items-center gap-2 group">
      {val ? (
        isLink ? (
          <a href={val} target="_blank" rel="noreferrer" className="truncate text-[#002FA7] hover:underline" title={val}>
            {val.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
          </a>
        ) : (
          <span className="truncate text-zinc-600" title={val}>{val}</span>
        )
      ) : (
        <span className="text-zinc-300">—</span>
      )}
      <button onClick={() => { setValue(val); setEditing(true); }} data-testid={`edit-${field}-${lead.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-[#002FA7] shrink-0"><PencilSimple size={14} /></button>
    </div>
  );
}

function SortTh({ label, col, sortKey, sortDir, onSort }) {
  const active = sortKey === col;
  return (
    <th className="px-3 py-3 font-medium">
      <button
        onClick={() => onSort(col)}
        data-testid={`sort-${col}`}
        className={`flex items-center gap-1 uppercase tracking-wider transition-colors duration-150 ${
          active ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
        }`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />
        ) : (
          <ArrowsDownUp size={12} className="opacity-30" />
        )}
      </button>
    </th>
  );
}
